import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import type { LessonStep } from '@/courses/LessonContent';
import { LessonContent } from '@/courses/LessonContent';
import { useCourseContext } from '~/contexts/courseContext';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute('/_app/_course/lessons/$lessonSlug')({
  component: LessonScreen,
});

function LessonScreen() {
  const { lessonSlug } = Route.useParams();
  const { setSelectedLessonSlug, setExpandedLessons, setSelectedTopicSlug } =
    useCourseContext();
  const { me, isStudent } = useCurrentUser();
  const { t } = useTranslation();
  const language = me?.language ?? 'EN';

  useEffect(() => {
    setSelectedLessonSlug(lessonSlug);
    setExpandedLessons((prev) =>
      prev.includes(lessonSlug) ? prev : [...prev, lessonSlug],
    );
    setSelectedTopicSlug('');
  }, [
    lessonSlug,
    setSelectedLessonSlug,
    setExpandedLessons,
    setSelectedTopicSlug,
  ]);

  const { data: resolved } = api.lessonBySlug.useQuery({
    path: { slug: lessonSlug },
    query: { language },
  });

  const lessonId = resolved?.id;

  const { data: lessonDetail } = api.lessonDetail.useQuery(
    { path: { lesson_id: lessonId ?? 0 } },
    { enabled: lessonId != null },
  );

  const { data: topicsData } = api.lessonTopics.useQuery(
    { path: { lesson_id: lessonId ?? 0 }, query: { limit: 50 } },
    { enabled: lessonId != null },
  );

  const courseId = lessonDetail?.course_id;

  const { data: courseDetail } = api.courseDetail.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  // Same parameters as the layout's query so the two share one cache entry and
  // one request; all this screen needs off it is the paginated `count`, which is
  // the "of M" denominator.
  const { data: courseLessons } = api.courseLessons.useQuery(
    { path: { course_id: courseId ?? 0 }, query: { limit: 50 } },
    { enabled: courseId != null },
  );

  const { data: viewerState } = api.courseViewerState.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: courseProgress } = api.courseProgress.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null && isStudent },
  );

  const courseSlug = courseDetail?.slug;

  // `lesson.php`'s 302: legacy sends a no-access lesson GET to the parent COURSE
  // (measured with X_WBS_DEV_BYPASS and no cookies:
  // `/lessons/god-has-spoken-through-nature/` -> 302 -> `/courses/god-has-spoken/`,
  // identical for a logged-in student with no grant). Nothing on this screen is
  // access-gated server-side — `lesson_by_slug` / `lesson_detail` / `lesson_topics`
  // return the same content TREE `/all-courses` already publishes — so the
  // redirect is driven by `course_viewer_state`, which is ungated and answers
  // `has_access = true` unconditionally for a non-student (legacy's preview mode).
  if (viewerState?.has_access === false && courseSlug) {
    return (
      <Navigate to="/courses/$courseSlug/" params={{ courseSlug }} replace />
    );
  }

  if (!lessonDetail || !topicsData) {
    return null;
  }

  const lessonProgress = courseProgress
    ? courseProgress[String(lessonId)]
    : undefined;
  // The completed SET, not the ordinal pointer. `learndash-sidebar.php:234`
  // reads a per-topic set, and the two integers on `course_completions` cannot
  // represent one: measured 2026-08-17 the completed set has a hole inside a
  // lesson on 6 (user, lesson) pairs, where an `index < topics_completed` prefix
  // rule marks the wrong topics done.
  const completedTopicIds = new Set(lessonProgress?.completed_topic_ids ?? []);

  // Sort by order so display and completion don't depend on API ordering
  const orderedTopics = [...(topicsData.data ?? [])].sort(
    (a, b) => a.order - b.order,
  );
  const steps: LessonStep[] = orderedTopics.map((topic) => ({
    slug: topic.slug,
    title: topic.title,
    type: 'topic',
    completed: lessonProgress ? completedTopicIds.has(topic.id) : undefined,
  }));

  // The lesson's own final quiz had no route from this page at all. The flag is
  // "EXISTS a PUBLISHED question with `topic_id IS NULL`" (456 of 543 lessons),
  // deliberately NOT a `quiz_ids` row (473): 17 PUBLISHED lessons carry a quiz row
  // whose questions are all DRAFT, and routing into that form gives a 0-question
  // quiz that scores 0 and fails. It is not derived from `lessonQuestions` either
  // — that read is quiz-locked, so it 403s for exactly the students who have not
  // finished the topics, i.e. the ones on this page.
  if (lessonDetail.has_final_quiz) {
    // Labelled from the catalogue rather than with the quiz post's own title:
    // `quiz_ids` has a `title` column but no v3 read on this screen serves it,
    // and `lessons.quiz` is already translated in all four languages.
    steps.push({ slug: lessonSlug, title: t('lessons.quiz'), type: 'quiz' });
  }

  const isLessonCompleted = lessonProgress?.completed ?? false;

  return (
    <LessonContent
      courseTitle={courseDetail?.title ?? ''}
      courseSlug={courseSlug ?? ''}
      lessonId={lessonId}
      lessonTitle={lessonDetail.title}
      lessonSlug={lessonSlug}
      lessonIndex={lessonDetail.index}
      lessonsTotal={courseLessons?.count ?? 0}
      topics={steps}
      isStudent={isStudent}
      isCompleted={isLessonCompleted}
    />
  );
}
